import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import clsx from 'clsx';
import { ArrowLeftRight, Camera, Check, CloudOff, Image, ImageOff, Maximize2, Plus, Receipt, RefreshCw, Trash } from 'lucide-react';
import toast from 'react-hot-toast';
import { useAppSelector } from '@/app/hooks';
import api, { getErrorMessage } from '@/services/api';
import { ApiResponse, PagedResponse, Reimbursement, ReimbursementStatus, ReimbursementWallet, TransferStatus } from '@/services/types';
import { API_ENDPOINTS } from '@/utils/constants';
import { formatCurrency, formatFileSize } from '@/utils/formatters';
import { classifyPettyCashError } from '@/utils/pettyCash';
import { Alert, Badge, Button, Chip, IconButton, Input, LoadingSkeleton, NumericKeypad, Overline, Select, Sheet } from '@/components/ui';
import { PageShell } from '@/components/layout/PageShell';
import { useIsLandscape } from '@/hooks/useMediaQuery';

const MAX_IMAGE_BYTES = 5 * 1024 * 1024;
const PAGE_SIZE = 20;
const QUICK_AMOUNTS = [10000, 20000, 50000, 100000];
type Filter = 'ALL' | ReimbursementStatus;
const FILTERS: Filter[] = ['ALL', 'PENDING', 'APPROVED', 'REJECTED'];
const STATUS_TONE: Record<ReimbursementStatus, 'warning' | 'success' | 'danger'> = { PENDING: 'warning', APPROVED: 'success', REJECTED: 'danger' };
const TRANSFER_TONE: Record<TransferStatus, 'success' | 'warning' | 'danger'> = { PAID: 'success', NEEDAUTHORIZATION: 'warning', VOID: 'danger' };

/** Reimbursement routes return meta.errors as a plain string on failure. */
const apiError = (err: unknown) => {
    if (axios.isAxiosError(err)) {
        const errors = err.response?.data?.meta?.errors;
        if (typeof errors === 'string' && errors) return errors;
    }
    return getErrorMessage(err);
};

/** Timestamps are naive server-local strings; formatters' toLocalDate would shift them by +7h. */
const whenOf = (iso: string) => new Date(iso).toLocaleString('id-ID', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });

const openProof = (url: string) => window.open(url, '_blank', 'noopener');

const routeOf = (r: Reimbursement) =>
    `${r.sourceAccountName ?? r.sourceAccountNumber} (${r.sourceAccountNumber}) → ${r.destinationAccountName ?? r.destinationAccountNumber} (${r.destinationAccountNumber})`;

/**
 * Reimbursement — filterable list with a detail pane (lg) / sheet (phone), and a submit form
 * (photo · name/desc/wallets · amount) as a second view. Mirrors PettyCashPage minus the balance hero.
 */
const ReimbursementPage: React.FC = () => {
    const { t } = useTranslation();
    const { selectedShop } = useAppSelector((s) => s.auth);
    const isLandscape = useIsLandscape();
    const [wallets, setWallets] = useState<ReimbursementWallet[]>([]);
    const [entries, setEntries] = useState<Reimbursement[]>([]);
    const [page, setPage] = useState(0);
    const [last, setLast] = useState(true);
    const [filter, setFilter] = useState<Filter>('ALL');
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [selectedId, setSelectedId] = useState<number | null>(null);
    const [detailOpen, setDetailOpen] = useState(false);
    const [view, setView] = useState<'list' | 'submit'>('list');
    // submit form
    const [photo, setPhoto] = useState<File | null>(null);
    const [preview, setPreview] = useState<string | null>(null);
    const [name, setName] = useState('');
    const [amount, setAmount] = useState('');
    const [description, setDescription] = useState('');
    const [source, setSource] = useState('');
    const [destination, setDestination] = useState('');
    const [photoError, setPhotoError] = useState<string | null>(null);
    const [nameError, setNameError] = useState<string | null>(null);
    const [amountError, setAmountError] = useState<string | null>(null);
    const [walletError, setWalletError] = useState<string | null>(null);
    const [destinationError, setDestinationError] = useState<string | null>(null);
    const [formError, setFormError] = useState<string | null>(null);
    const [uploadFailed, setUploadFailed] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const fileRef = useRef<HTMLInputElement>(null);

    const shopId = selectedShop?.shopId;

    const loadWallets = useCallback(async () => {
        if (!shopId) return;
        try {
            const res = await api.get<ApiResponse<ReimbursementWallet[]>>(API_ENDPOINTS.REIMBURSEMENT_WALLETS(shopId));
            const list = res.data.data || [];
            setWallets(list);
            // The owner may have un-flagged the selected wallet.
            const known = (n: string) => list.some((w) => w.accountNumber === n);
            setSource((s) => (known(s) ? s : ''));
            setDestination((d) => (known(d) ? d : ''));
        } catch (e) {
            setError(apiError(e));
        }
    }, [shopId]);

    const load = useCallback(async (p: number, replace: boolean) => {
        if (!shopId) return;
        setLoading(true);
        setError(null);
        try {
            const res = await api.get<ApiResponse<PagedResponse<Reimbursement>>>(API_ENDPOINTS.REIMBURSEMENT(shopId), {
                params: { status: filter === 'ALL' ? undefined : filter, page: p, size: PAGE_SIZE },
            });
            const content = res.data.data.content || [];
            setEntries((prev) => (replace ? content : [...prev, ...content]));
            setLast(res.data.data.page?.last ?? content.length < PAGE_SIZE);
            setPage(p);
        } catch (e) {
            setError(apiError(e));
        } finally {
            setLoading(false);
        }
    }, [shopId, filter]);

    const refresh = useCallback(() => { loadWallets(); load(0, true); }, [loadWallets, load]);

    useEffect(() => { refresh(); }, [refresh]);
    useEffect(() => {
        if (!photo) { setPreview(null); return; }
        const url = URL.createObjectURL(photo);
        setPreview(url);
        return () => URL.revokeObjectURL(url);
    }, [photo]);

    if (!selectedShop || !shopId) return null;

    const selected = entries.find((e) => e.id === selectedId) ?? (isLandscape ? entries[0] : undefined) ?? null;
    const amountNum = Number(amount || 0);
    const photoTooLarge = !!photo && photo.size > MAX_IMAGE_BYTES;
    const noWallets = wallets.length === 0;
    const sameWallet = !!source && source === destination;
    const canSubmit = !!photo && !photoTooLarge && name.trim().length > 0 && name.trim().length <= 150 && Number.isInteger(amountNum) && amountNum > 0 && !!source && !!destination && !sameWallet && !noWallets;

    const walletOptions = wallets.map((w) => ({
        value: w.accountNumber,
        label: `${w.accountName} (${w.accountNumber}) · ${w.paymentAccountType === 'MEDSHOPPOINT' ? t('reimbursement.walletShop') : t('reimbursement.walletOwner')}`,
    }));

    const pickPhoto = (f: File | null) => {
        setPhoto(f);
        setPhotoError(f && f.size > MAX_IMAGE_BYTES ? t('reimbursement.photoTooLarge') : null);
        setUploadFailed(false);
    };
    const resetForm = () => { setPhoto(null); setName(''); setAmount(''); setDescription(''); setSource(''); setDestination(''); setPhotoError(null); setNameError(null); setAmountError(null); setWalletError(null); setDestinationError(null); setFormError(null); setUploadFailed(false); };
    const openSubmit = () => { resetForm(); setView('submit'); };
    const statusLabel = (s: ReimbursementStatus) => t(`reimbursement.status.${s}` as 'reimbursement.status.PENDING');
    const transferLabel = (s: TransferStatus) => t(`reimbursement.transfer.${s}` as 'reimbursement.transfer.PAID');

    const submit = async () => {
        setFormError(null); setNameError(null); setAmountError(null); setWalletError(null); setDestinationError(null);
        if (!photo) { setPhotoError(t('reimbursement.photoRequired')); return; }
        if (photoTooLarge) { setPhotoError(t('reimbursement.photoTooLarge')); return; }
        if (!name.trim()) { setNameError(t('reimbursement.nameRequired')); return; }
        if (!Number.isInteger(amountNum) || amountNum <= 0) { setAmountError(t('reimbursement.amountInvalid')); return; }
        if (sameWallet) { setDestinationError(t('reimbursement.sameWallet')); return; }
        setSubmitting(true);
        try {
            const form = new FormData();
            form.append('proof', photo, 'proof.jpg');
            form.append('name', name.trim());
            form.append('amount', String(amountNum));
            if (description.trim()) form.append('description', description.trim());
            form.append('sourceAccountNumber', source);
            form.append('destinationAccountNumber', destination);
            const res = await api.post<ApiResponse<Reimbursement>>(API_ENDPOINTS.REIMBURSEMENT(shopId), form, { headers: { 'Content-Type': 'multipart/form-data' } });
            toast.success(t('reimbursement.submitted', { name: res.data.data.name, amount: formatCurrency(res.data.data.amount) }));
            resetForm();
            setView('list');
            if (filter === 'ALL') refresh(); else setFilter('ALL'); // filter change refetches via the effect
        } catch (e) {
            const msg = apiError(e);
            switch (classifyPettyCashError(msg)) {
                case 'photoRequired': setPhotoError(t('reimbursement.photoRequired')); setPhoto(null); break;
                case 'photoTooLarge': setPhotoError(t('reimbursement.photoTooLarge')); setPhoto(null); break;
                case 'uploadFailed': setUploadFailed(true); break;
                case 'photo': setPhotoError(msg); setPhoto(null); break;
                case 'name': setNameError(msg); break;
                case 'amount': setAmountError(msg); break;
                case 'wallet': setWalletError(t('reimbursement.walletNotSelectable')); loadWallets(); break;
                case 'sameWallet': setDestinationError(t('reimbursement.sameWallet')); break;
                case 'inactive': setFormError(t('reimbursement.inactiveUser')); break;
                default: setFormError(msg);
            }
        } finally {
            setSubmitting(false);
        }
    };

    const transferChip = (r: Reimbursement) => r.status === 'APPROVED' && r.transferStatus ? <Badge tone={TRANSFER_TONE[r.transferStatus]}>{transferLabel(r.transferStatus)}</Badge> : null;

    const detail = (r: Reimbursement) => {
        const facts: [string, string][] = [
            [t('reimbursement.recordedBy'), r.submittedByName ?? '—'],
            [t('reimbursement.time'), whenOf(r.createdAt)],
            [t('reimbursement.reviewedBy'), r.reviewedByName ?? t('reimbursement.notReviewed')],
            [t('reimbursement.reviewedAt'), r.reviewedAt ? whenOf(r.reviewedAt) : '—'],
        ];
        return (
            <div className="flex flex-col gap-4">
                <div className="flex items-center gap-2.5">
                    <Overline className="flex-1">#{r.id}</Overline>
                    {transferChip(r)}
                    <Badge tone={STATUS_TONE[r.status]} dot>{statusLabel(r.status)}</Badge>
                </div>
                <div className="flex flex-col gap-1">
                    <span className="text-[38px] font-black tnum leading-[1.1] tracking-[-0.02em] text-ink-brand">{formatCurrency(r.amount)}</span>
                    <span className="text-[19px] font-black">{r.name}</span>
                    <span className="text-[13px] text-ink-muted">{routeOf(r)}</span>
                    {r.description && <span className="text-sm leading-relaxed text-ink-secondary">{r.description}</span>}
                </div>
                {r.status === 'REJECTED' && r.reviewerNote && <Alert variant="error" title={t('reimbursement.rejectedNote')} message={r.reviewerNote} />}
                {r.status === 'PENDING' && <Alert variant="warning" message={t('reimbursement.pendingHint')} />}
                {r.proofImageUrl && (
                    <button type="button" onClick={() => openProof(r.proofImageUrl)} className="relative h-[168px] shrink-0 overflow-hidden rounded-lg border border-line-subtle bg-surface-sunken">
                        <img src={r.proofImageUrl} alt="" className="h-full w-full object-cover" />
                        <span className="absolute bottom-2.5 right-2.5 flex h-[30px] items-center gap-1.5 rounded-pill bg-[#2d1227]/70 px-3 text-xs font-extrabold text-white backdrop-blur"><Maximize2 className="h-[13px] w-[13px]" />{t('reimbursement.viewProof')}</span>
                    </button>
                )}
                <div className="flex flex-col border-t border-line-subtle">
                    {facts.map(([k, v]) => (
                        <div key={k} className="flex justify-between gap-3 border-b border-line-subtle py-2.5 text-sm"><span className="text-ink-muted">{k}</span><span className="text-right font-extrabold">{v}</span></div>
                    ))}
                </div>
            </div>
        );
    };

    if (view === 'submit') {
        const photoSlot = photoTooLarge ? (
            <div className="flex min-h-[220px] flex-1 flex-col items-center justify-center gap-3 rounded-xl border-2 border-status-danger bg-status-danger-soft p-6 text-center">
                <span className="flex h-16 w-16 items-center justify-center rounded-full bg-surface-card text-status-danger"><ImageOff className="h-7 w-7" /></span>
                <span className="text-[17px] font-black text-danger-700">{t('reimbursement.photoTooLarge')} · {formatFileSize(photo!.size)}</span>
                <span className="text-[13px] leading-relaxed">{t('reimbursement.photoTooLargeHint')}</span>
            </div>
        ) : preview ? (
            <div className="relative min-h-[220px] flex-1 overflow-hidden rounded-xl border border-line-subtle bg-surface-sunken">
                <img src={preview} alt="" className="absolute inset-0 h-full w-full object-cover" />
                <span className="absolute left-3 top-3 flex h-7 items-center gap-1.5 rounded-pill bg-surface-card px-2.5 text-xs font-extrabold text-success-700 shadow-xs"><Check className="h-[13px] w-[13px]" />{formatFileSize(photo!.size)}</span>
            </div>
        ) : (
            <button type="button" onClick={() => fileRef.current?.click()} className={clsx('flex min-h-[220px] flex-1 flex-col items-center justify-center gap-3 rounded-xl border-2 border-dashed bg-surface-card p-6 text-center hover:border-accent-500 hover:bg-accentsoft', photoError ? 'border-status-danger' : 'border-line')}>
                <span className="flex h-[72px] w-[72px] items-center justify-center rounded-full bg-accent-500 text-ink-onAccent shadow-accent"><Camera className="h-[30px] w-[30px]" /></span>
                <span className="text-[17px] font-black">{t('reimbursement.takePhoto')}</span>
                <span className="text-[13px] leading-relaxed text-ink-muted">{t('reimbursement.photoHint')}</span>
            </button>
        );
        const cta = <Button variant="primary" size="lg" fullWidth disabled={!canSubmit} loading={submitting} onClick={submit}>{uploadFailed ? t('reimbursement.resend') : t('reimbursement.send')}</Button>;
        return (
            <PageShell
                close
                title={t('reimbursement.submit')}
                subtitle={selectedShop.shopName}
                onBack={() => setView('list')}
                footer={<div className="lg:hidden">{cta}</div>}
            >
                <input ref={fileRef} type="file" accept="image/*" capture="environment" className="hidden" onChange={(e) => { pickPhoto(e.target.files?.[0] ?? null); e.target.value = ''; }} />
                <div className="flex flex-col gap-4 pb-5 lg:grid lg:h-full lg:grid-cols-[340px_minmax(0,1fr)_360px] lg:gap-5 lg:pb-6">
                    <div className="flex flex-col gap-2.5 lg:min-h-0">
                        <span className="text-[13px] font-extrabold text-ink-secondary">{t('reimbursement.photo')} <span className="text-status-danger">*</span></span>
                        {photoSlot}
                        {photoError && !photoTooLarge && <p className="text-xs font-semibold text-status-danger">{photoError}</p>}
                        {photo && (
                            <div className="flex gap-2.5">
                                <Button variant={photoTooLarge ? 'primary' : 'secondary'} fullWidth icon={<Camera />} onClick={() => fileRef.current?.click()}>{t('reimbursement.retake')}</Button>
                                {!photoTooLarge && <Button variant="ghost" icon={<Trash />} onClick={() => pickPhoto(null)}>{t('reimbursement.remove')}</Button>}
                            </div>
                        )}
                    </div>
                    <div className="flex flex-col gap-4 lg:min-w-0">
                        {noWallets && <Alert variant="warning" message={t('reimbursement.noWallets')} />}
                        {uploadFailed && <Alert variant="error" title={t('reimbursement.uploadFailed')} message={<span className="flex items-center gap-2"><CloudOff className="h-4 w-4 shrink-0" />{t('reimbursement.uploadFailedHint')}</span>} />}
                        {formError && <Alert variant="error" message={formError} />}
                        <Input label={t('reimbursement.name')} required maxLength={150} value={name} onChange={(e) => { setName(e.target.value); setNameError(null); }} placeholder={t('reimbursement.namePlaceholder')} error={nameError ?? undefined} helperText={`${name.length}/150`} fieldSize="lg" className="!font-extrabold" />
                        <Select label={t('reimbursement.sourceWallet')} required options={walletOptions} placeholder={t('reimbursement.walletPlaceholder')} value={source} disabled={noWallets} onChange={(e) => { setSource(e.target.value); setWalletError(null); setDestinationError(null); }} error={walletError ?? undefined} />
                        <Select label={t('reimbursement.destinationWallet')} required options={walletOptions} placeholder={t('reimbursement.walletPlaceholder')} value={destination} disabled={noWallets} onChange={(e) => { setDestination(e.target.value); setWalletError(null); setDestinationError(null); }} error={destinationError ?? walletError ?? (sameWallet ? t('reimbursement.sameWallet') : undefined)} />
                        <Input label={`${t('reimbursement.description')} ${t('reimbursement.optional')}`} multiline rows={3} value={description} onChange={(e) => setDescription(e.target.value)} placeholder={t('reimbursement.descriptionPlaceholder')} />
                        <div className="hidden flex-1 lg:block" />
                        <Alert variant="brand" message={t('reimbursement.submitHint')} />
                    </div>
                    <div className="flex flex-col gap-3 rounded-xl border border-line-subtle bg-surface-card p-4 shadow-sm lg:min-h-0">
                        <Input
                            label={t('reimbursement.amount')}
                            required
                            numeric
                            inputMode="numeric"
                            placeholder="0"
                            value={amount}
                            onChange={(e) => { setAmount(e.target.value.replace(/\D/g, '').replace(/^0+(?=\d)/, '').slice(0, 12)); setAmountError(null); }}
                            error={amountError ?? undefined}
                            leftIcon={<span className="text-xl font-extrabold text-ink-muted">Rp</span>}
                            wrapperClassName="!h-16 !border-2 !border-accent-500 shadow-focus lg:!h-[68px]"
                            className="!text-[28px] lg:!text-[32px]"
                        />
                        <div className="flex flex-wrap gap-2">
                            {QUICK_AMOUNTS.map((v) => <Chip key={v} selected={amountNum === v} onClick={() => setAmount(String(v))}>{formatCurrency(v)}</Chip>)}
                        </div>
                        <NumericKeypad className="hidden min-h-0 flex-1 lg:grid" value={amount} onChange={setAmount} />
                        <div className="hidden lg:block">{cta}</div>
                    </div>
                </div>
            </PageShell>
        );
    }

    const submitButton = (props: { size?: 'md' | 'lg'; fullWidth?: boolean; className?: string }) => (
        <Button variant="primary" icon={<Plus />} onClick={openSubmit} disabled={noWallets} {...props}>{t('reimbursement.submit')}</Button>
    );

    return (
        <div className="flex h-full min-h-0 flex-1">
            <PageShell
                title={t('reimbursement.title')}
                subtitle={selectedShop.shopName}
                backTo="/settings"
                className="lg:border-r lg:border-line-subtle"
                actions={<>
                    <IconButton variant="card" label={t('common.refresh')} onClick={refresh} disabled={loading}><RefreshCw className={clsx(loading && 'animate-spin')} /></IconButton>
                    {submitButton({ className: 'hidden lg:inline-flex' })}
                </>}
                footer={<div className="lg:hidden">{submitButton({ size: 'lg', fullWidth: true })}</div>}
            >
                <div className="flex flex-col gap-3 pb-5">
                    {error && <Alert variant="error" message={error} />}
                    {!error && noWallets && <Alert variant="warning" message={t('reimbursement.noWallets')} />}
                    <div className="flex gap-2 overflow-x-auto">
                        {FILTERS.map((f) => (
                            <Chip key={f} selected={filter === f} onClick={() => setFilter(f)}>{f === 'ALL' ? t('reimbursement.filterAll') : statusLabel(f)}</Chip>
                        ))}
                    </div>
                    {loading && entries.length === 0 && [1, 2, 3].map((i) => <LoadingSkeleton key={i} height={72} />)}
                    {!loading && entries.length === 0 && (
                        <div className="flex flex-col items-center gap-3 py-12 text-center">
                            <span className="flex h-[88px] w-[88px] items-center justify-center rounded-full bg-brandsoft text-ink-brand"><ArrowLeftRight className="h-[38px] w-[38px]" /></span>
                            <span className="text-[22px] font-black">{t('reimbursement.empty')}</span>
                            <span className="max-w-[380px] text-[15px] leading-relaxed text-ink-secondary">{t('reimbursement.emptyHint')}</span>
                        </div>
                    )}
                    {entries.map((r) => {
                        const active = isLandscape && selected?.id === r.id;
                        return (
                            <button
                                key={r.id}
                                type="button"
                                onClick={() => { setSelectedId(r.id); if (!isLandscape) setDetailOpen(true); }}
                                className={clsx('flex items-center gap-3.5 rounded-lg px-4 py-3 text-left', active ? 'border-2 border-accent-500 bg-accentsoft' : 'border border-line-subtle bg-surface-card')}
                            >
                                <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-surface-sunken text-ink-brand"><ArrowLeftRight className="h-5 w-5" /></span>
                                <span className="flex min-w-0 flex-1 flex-col gap-[3px]">
                                    <span className="truncate text-base font-extrabold">{r.name}</span>
                                    <span className="flex items-center gap-1.5 text-[13px] text-ink-muted">{r.proofImageUrl && <Image className="h-3.5 w-3.5" />}{r.submittedByName ?? '—'} · {whenOf(r.createdAt)}</span>
                                    <span className="truncate text-xs text-ink-muted">{routeOf(r)}</span>
                                </span>
                                <span className="flex flex-col items-end gap-1.5">
                                    <span className="text-base font-black tnum">{formatCurrency(r.amount)}</span>
                                    <span className="flex items-center gap-1.5">{transferChip(r)}<Badge tone={STATUS_TONE[r.status]} dot>{statusLabel(r.status)}</Badge></span>
                                </span>
                            </button>
                        );
                    })}
                    {!last && <Button variant="outline" onClick={() => load(page + 1, false)} loading={loading} className="mt-2 self-center">{t('common.loadMore')}</Button>}
                </div>
            </PageShell>

            <aside className="hidden w-[420px] shrink-0 overflow-y-auto bg-surface-card p-6 lg:block">
                {selected ? detail(selected) : (
                    <div className="flex h-full flex-col items-center justify-center gap-2 text-center text-ink-muted">
                        <Receipt className="h-9 w-9 text-primary-200" />
                        <p className="text-sm font-bold">{t('reimbursement.selectHint')}</p>
                    </div>
                )}
            </aside>

            <Sheet isOpen={detailOpen && !isLandscape && !!selected} onClose={() => setDetailOpen(false)} showCloseButton={false}>
                {selected && detail(selected)}
            </Sheet>
        </div>
    );
};

export default ReimbursementPage;
